/**
 * Renders agent status time chart tooltips in a portal (avoids widget overflow clipping).
 * Portals to document.fullscreenElement when the dashboard is fullscreen — body-only
 * portals are invisible because only the fullscreen subtree is displayed.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';

const VIEWPORT_PAD = 10;
const GAP = 10;

function getPortalRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return (document.fullscreenElement as HTMLElement | null) ?? document.body;
}

function getViewportBounds(): { top: number; left: number; width: number; height: number } {
  const fs = document.fullscreenElement;
  if (fs) {
    const r = fs.getBoundingClientRect();
    return { top: r.top, left: r.left, width: r.width, height: r.height };
  }
  return { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
}

export function StatusTimePortalTooltip({
  active,
  coordinate,
  chartRef,
  children,
}: {
  active?: boolean;
  coordinate?: { x?: number; y?: number };
  chartRef: RefObject<HTMLElement | null>;
  children: ReactNode;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [portalRoot, setPortalRoot] = useState<HTMLElement | null>(() =>
    typeof document !== 'undefined' ? getPortalRoot() : null
  );
  const [style, setStyle] = useState<{
    top: number;
    left: number;
    transform: string;
  } | null>(null);

  useEffect(() => {
    const sync = () => setPortalRoot(getPortalRoot());
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, []);

  useLayoutEffect(() => {
    if (!active || !coordinate || coordinate.x == null || coordinate.y == null) {
      setStyle(null);
      return;
    }
    const chartEl = chartRef.current;
    if (!chartEl) {
      setStyle(null);
      return;
    }

    const place = () => {
      const chartRect = chartEl.getBoundingClientRect();
      const anchorX = chartRect.left + coordinate.x!;
      const anchorY = chartRect.top + coordinate.y!;

      const tipEl = tooltipRef.current;
      const tipW = tipEl?.offsetWidth ?? 180;
      const tipH = tipEl?.offsetHeight ?? 160;

      const vp = getViewportBounds();
      const vpRight = vp.left + vp.width;
      const vpBottom = vp.top + vp.height;

      let left = anchorX;
      const top = anchorY;
      let transform = `translate(-50%, calc(-100% - ${GAP}px))`;

      const spaceAbove = anchorY - vp.top - VIEWPORT_PAD;
      const spaceBelow = vpBottom - anchorY - VIEWPORT_PAD;
      if (spaceAbove < tipH + GAP && spaceBelow > spaceAbove) {
        transform = `translate(-50%, ${GAP}px)`;
      }

      const halfW = tipW / 2;
      if (left - halfW < vp.left + VIEWPORT_PAD) {
        left = vp.left + VIEWPORT_PAD + halfW;
      } else if (left + halfW > vpRight - VIEWPORT_PAD) {
        left = vpRight - VIEWPORT_PAD - halfW;
      }

      setStyle({ top, left, transform });
    };

    place();
    const id = requestAnimationFrame(place);
    return () => cancelAnimationFrame(id);
  }, [active, coordinate?.x, coordinate?.y, chartRef, portalRoot]);

  if (
    !active ||
    !coordinate ||
    coordinate.x == null ||
    coordinate.y == null ||
    !children ||
    !portalRoot
  ) {
    return null;
  }

  const initialStyle = style ?? {
    top: (chartRef.current?.getBoundingClientRect().top ?? 0) + (coordinate.y ?? 0),
    left: (chartRef.current?.getBoundingClientRect().left ?? 0) + (coordinate.x ?? 0),
    transform: `translate(-50%, calc(-100% - ${GAP}px))`,
  };

  const node = (
    <div
      ref={tooltipRef}
      role="tooltip"
      style={{
        position: 'fixed',
        top: initialStyle.top,
        left: initialStyle.left,
        transform: initialStyle.transform,
        zIndex: 10000,
        pointerEvents: 'none',
      }}
    >
      {children}
    </div>
  );

  return createPortal(node, portalRoot);
}
